import React, { useState, useCallback, useEffect, useRef } from "react";
import { Modal, Button, Form, Alert, Row, Col } from "react-bootstrap";

// Timer settings
const RESEND_INTERVAL_SECONDS = 30;
const OTP_LENGTH = 6;

/**
 * Aadhaar OTP Verification Modal Component
 * with 6 separate input boxes.
 * * @param {object} props
 * @param {boolean} props.show - Controls the visibility of the modal
 * @param {function} props.onHide - Function to close the modal
 * @param {function} props.onVerifyOtp - Function to call on OTP submission
 * @param {function} props.onResendOtp - Function to call on Resend OTP
 */
function AadhaarOtpModal({ show, onHide, onVerifyOtp, onResendOtp }) {
  // State for the 6 individual OTP digits
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(RESEND_INTERVAL_SECONDS);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  // Refs for focusing the inputs
  const inputRefs = useRef([]);

  // Combine the individual digits into a single string
  const fullOtp = otp.join("");
  const isOtpValid = fullOtp.length === OTP_LENGTH && /^\d+$/.test(fullOtp);

  // Handler for input change
  const handleChange = (element, index) => {
    // Only allow single numeric digit
    if (isNaN(element.value)) return;

    const newOtp = [...otp];
    newOtp[index] = element.value.slice(-1); // Take only the last character (if pasted)
    setOtp(newOtp);

    // Focus next input
    if (element.value !== "" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handler for backspace/delete key
  const handleKeyDown = (element, index) => {
    // If pressing backspace/delete on an empty box, move to the previous box
    if (element.key === "Backspace" && otp[index] === "") {
      if (index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  // Function to handle OTP submission
  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");

    if (!isOtpValid) {
      setError("Please enter a valid 6-digit OTP.");
      return;
    }

    setIsLoading(true);
    // Call the parent component's verification function
    onVerifyOtp(fullOtp).finally((data) => {
      setIsLoading(false);
      // Do not clear OTP on failure, let the user correct it
    });
  };

  // Function to handle Resend OTP
  const handleResend = useCallback(() => {
    setError("");
    // Clear the current OTP input
    setOtp(new Array(OTP_LENGTH).fill(""));
    // Focus the first input box
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    // Call the parent component's resend function
    onResendOtp();

    // Reset the timer and disable resend button
    setResendTimer(RESEND_INTERVAL_SECONDS);
    setIsResendDisabled(true);
  }, [onResendOtp]);

  // Effect for the Resend OTP countdown timer
  useEffect(() => {
    let interval;
    if (show && isResendDisabled && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prevTime) => prevTime - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setIsResendDisabled(false);
      clearInterval(interval);
    }

    // Cleanup function
    return () => clearInterval(interval);
  }, [show, isResendDisabled, resendTimer]);

  // Effect to reset state when the modal is closed
  useEffect(() => {
    if (!show) {
      setOtp(new Array(OTP_LENGTH).fill(""));
      setError("");
      setIsLoading(false);
      setResendTimer(RESEND_INTERVAL_SECONDS); // Reset timer on close
      setIsResendDisabled(true);
    } else {
      // Focus on the first input when the modal opens
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 0);
    }
  }, [show]);

  return (
    <Modal backdrop="static" show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Aadhaar OTP Verification</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="text-muted">
            Enter the 6-digit OTP sent to your registered mobile number.
          </p>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form.Group className="mb-4">
            <Row className="justify-content-center g-2 g-md-3">
              {otp.map((data, index) => (
                <Col key={index} xs={2}>
                  <Form.Control
                    type="text"
                    name={`otp-${index}`}
                    value={data}
                    onChange={(e) => handleChange(e.target, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={(e) => e.target.select()} // Select all text on focus
                    maxLength="1"
                    className="text-center font-weight-bold"
                    style={{ fontSize: "1.25rem", height: "3rem", padding: 0 }}
                    disabled={isLoading}
                    required
                    // Set ref for each input
                    ref={(el) => (inputRefs.current[index] = el)}
                  />
                </Col>
              ))}
            </Row>
          </Form.Group>

          <div className="d-grid gap-2">
            <Button
              variant="outline-primary"
              onClick={handleResend}
              disabled={isResendDisabled || isLoading}
            >
              {isResendDisabled
                ? `Resend OTP in ${resendTimer}s`
                : "Resend OTP"}
            </Button>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isLoading}>
            Close
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!isOtpValid || isLoading}
          >
            {isLoading ? "Verifying..." : "Verify OTP"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AadhaarOtpModal;
